import { ApiFilled, ReloadOutlined } from "@ant-design/icons";
import {
  Cancellation,
  Tenant,
  useAuthFetch,
  useHasRole,
} from "@p0security/frontend-core";
import * as firestoreProvider from "@p0security/frontend-core/react";
import { ErrorDisplay } from "@p0security/frontend-core/react";
import type { IncidentIoIntegrationDoc } from "@p0security/integrations-incidentio-shared";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import {
  Alert,
  Button,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Typography,
} from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import * as firestore from "firebase/firestore";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { IntegrationCard } from "../IntegrationCard";
import { OwnerAlert } from "../OwnerAlert";
import { IncidentIoIconUrl } from "../constants.tsx";

type FormValues = { apiKey: string };

type IncidentIoSchedule = {
  id: string;
  name: string;
  timezone: string;
};

const InstallForm: React.FC<{
  onInstall: (apiKey: string) => Promise<void>;
  isInstalling: boolean;
}> = ({ onInstall, isInstalling }) => {
  const [form] = Form.useForm<FormValues>();
  const handleSubmit = useCallback(
    async (values: FormValues) => await onInstall(values.apiKey),
    [onInstall]
  );
  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Form.Item
        label="API key"
        name="apiKey"
        rules={[
          { required: true, message: "An Incident.io API key is required" },
        ]}
      >
        <Input.Password placeholder="Enter Incident.io API key" />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={isInstalling}
          icon={<ApiFilled />}
        >
          Install integration
        </Button>
      </Form.Item>
    </Form>
  );
};

const InstallPrompt: React.FC<{ onShowForm: () => void }> = ({
  onShowForm,
}) => (
  <>
    <Typography.Paragraph>
      The API Key requires the following permissions:
    </Typography.Paragraph>
    <ul>
      <li>View data, like public incidents and organization settings</li>
      <li>Create incidents</li>
      <li>Edit incidents</li>
      <li>Manage organization settings</li>
      <li>View catalog types and entries</li>
      <li>Read schedules</li>
    </ul>
    <Typography.Paragraph>
      Instructions for creating an Incident.io API key can be found{" "}
      <a href="https://docs.incident.io/integrations/api-overview#where-can-i-find-the-api-keys">
        here.
      </a>
    </Typography.Paragraph>
    <Button icon={<ApiFilled />} onClick={onShowForm} type="primary">
      Install integration
    </Button>
  </>
);

/** Selects schedules that cause auto-approval, and syncs them to Firestore. */
const IncidentIoScheduleSelect: React.FC<{
  integration: firestoreProvider.KnownFirestoreDoc<IncidentIoIntegrationDoc>;
}> = ({ integration }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<any>();
  const authFetch = useAuthFetch(setError);
  const canEdit = useHasRole("owner");

  const onRefresh = useCallback(
    async (cancellation?: Cancellation) => {
      if (!canEdit) return;
      setIsRefreshing(true);
      try {
        await authFetch("integrations/incidentio/refresh", {
          method: "POST",
          cancellation,
        });
      } finally {
        setIsRefreshing(false);
      }
    },
    [authFetch, canEdit]
  );

  const { docs: schedules } =
    firestoreProvider.useFirestoreCollection<IncidentIoSchedule>(
      `integrations/incidentio/schedules`,
      { live: true, tenantAware: true }
    );

  const options = useMemo(
    () =>
      schedules?.map((s) => (
        <Select.Option key={s.data.id} label={s.data.name}>
          {s.data.name}
        </Select.Option>
      )),
    [schedules]
  );

  const currentlyWrittenIds = integration.doc?.data.selectedScheduleIds ?? [];

  const onUpdate = useMemo(
    () => async (selected: string[]) => {
      setIsUpdating(true);
      await firestore.updateDoc(integration.ref, {
        selectedScheduleIds: selected,
      } as IncidentIoIntegrationDoc);
      setIsUpdating(false);
    },
    [integration.ref]
  );

  useEffect(() => {
    const cancellation = new Cancellation();
    onRefresh(cancellation);
    return cancellation.cancel;
  }, [onRefresh]);

  const onClickRefresh = useCallback(() => onRefresh(), [onRefresh]);

  return error ? (
    <ErrorDisplay title="Could not fetch Incident.io schedules" error={error} />
  ) : (
    <Space direction="vertical" size="middle">
      <div>
        Select schedules for auto-approval:
        <div className="flex">
          {isUpdating || isRefreshing || options === undefined ? (
            <Spin />
          ) : (
            <>
              <Select
                mode="multiple"
                allowClear
                disabled={!canEdit}
                className="grow"
                placeholder="None selected"
                defaultValue={currentlyWrittenIds}
                onChange={onUpdate}
                optionFilterProp="label"
              >
                {options}
              </Select>
              {canEdit && (
                <GraphTooltip title="Fetch latest schedules from Incident.io">
                  <Button onClick={onClickRefresh}>
                    <ReloadOutlined />
                  </Button>
                </GraphTooltip>
              )}
            </>
          )}
        </div>
      </div>
    </Space>
  );
};

export const IncidentIo: React.FC<object> = () => {
  const tenantId = useContext(Tenant);
  const integrationData =
    firestoreProvider.useFirestoreDoc<IncidentIoIntegrationDoc>(
      `o/${tenantId}/integrations/incidentio`,
      { live: true }
    );
  const [isInstalling, setIsInstalling] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [showForm, setShowForm] = useState(false);
  const authFetch = useAuthFetch(setError);
  const canInstall = useHasRole("owner");
  const handleShowForm = useCallback(() => setShowForm(true), []);

  const handleInstall = useCallback(
    async (apiKey: string) => {
      setIsInstalling(true);
      setError(undefined);
      const response = await authFetch("integrations/incidentio/config", {
        method: "POST",
        json: { root: { _: { apiKey: { clearText: apiKey } } } },
      });
      setIsInstalling(false);
      if (response) setShowForm(false);
    },
    [authFetch]
  );

  const isInstalled = useMemo(
    () =>
      integrationData.doc?.data.root?.[SINGLETON_ITEM]?.state === "installed",
    [integrationData.doc?.data.root]
  );

  if (integrationData.loading) return <Spin />;

  return (
    <IntegrationCard
      integration="incidentio"
      logo={IncidentIoIconUrl}
      title="Incident.io"
      integrationDoc={integrationData.doc}
    >
      {error && <Alert type="error" message={error} />}
      {isInstalled && integrationData.doc ? (
        <Space direction="vertical" size="middle">
          <Typography.Text>
            This integration has been installed and is ready for use.
          </Typography.Text>
          <IncidentIoScheduleSelect integration={integrationData} />
        </Space>
      ) : showForm ? (
        <InstallForm onInstall={handleInstall} isInstalling={isInstalling} />
      ) : canInstall ? (
        <InstallPrompt onShowForm={handleShowForm} />
      ) : (
        <OwnerAlert />
      )}
    </IntegrationCard>
  );
};
